import { MultiSelectDropdown } from 'components/common'
import { memo, useEffect, useMemo, useState } from 'react'
import { fetchStores } from 'utils/api/storeApi'

type Props = {
  setValue: any
  resetForm: any
  error?: string
  label: string
  name: string
  required?: boolean
  defaultSelected?: string[]
  isActive?: boolean
  disabled?: boolean
  hintText?: string
  /**
   * Third-party stores belong to a separate flow, so they are excluded by
   * default. Pass `true` to list ONLY third-party stores (Master Data's
   * third-party product → stores mapping is the only such case).
   */
  isThirdPartyStore?: boolean
  /**
   * When provided, the fetched store list is narrowed down to only these
   * store IDs (e.g. Master Data's "In Stock Stores" field only allows
   * stores already present in "Activated Stores"). Omit to list every
   * fetched store, unrestricted.
   */
  allowedValues?: string[]
}

const MultiSelectStoreDropdown = ({
  setValue,
  error,
  label,
  name,
  required = false,
  defaultSelected,
  isActive,
  resetForm,
  disabled = false,
  isThirdPartyStore = false,
  hintText,
  allowedValues,
}: Props) => {
  const [options, setOptions] = useState<any>([])
  const [loading, setLoading] = useState<boolean>(true)

  const getStores = async () => {
    try {
      const { data } = await fetchStores({
        limit: 500,
        page: 1,
        isActive,
        isThirdPartyStore,
      })
      const stores = data?.docs?.map((store: any) => ({
        label: store.storeName,
        value: store._id,
        disabled: false,
      }))

      if (!stores || stores.length <= 0) {
        return setOptions([])
      }
      setOptions(stores)
    } catch (error) {
      setOptions([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getStores()
  }, [])

  // Narrow the fetched list down to allowedValues, when given, instead of
  // filtering at the API layer — the full list is already fetched, and
  // allowedValues is typically a small, form-local set (e.g. another
  // field's current selection) that changes independently of the store
  // data itself.
  const visibleOptions = useMemo(() => {
    if (!allowedValues) return options

    const allowedSet = new Set(allowedValues)
    return options.filter((option: { value: string }) =>
      allowedSet.has(option.value),
    )
  }, [options, allowedValues])

  if (loading) {
    return <></>
  }

  return (
    <MultiSelectDropdown
      required={required}
      label={label}
      name={name}
      setValue={setValue}
      error={error}
      hintText={hintText}
      includeSelectAll={visibleOptions?.length > 0}
      defaultSelected={defaultSelected}
      disabled={disabled}
      options={
        visibleOptions.length <= 0
          ? [
              {
                label: allowedValues
                  ? 'No Stores Available'
                  : 'No Stores Found',
                value: 'NO_STORES',
                disabled: true,
              },
            ]
          : visibleOptions
      }
      reset={resetForm}
    />
  )
}

export default memo(MultiSelectStoreDropdown)
